import { useEffect, useState } from 'react';

import useDebounceCallback from '@lib/core/src/hooks/useDebounceCallback';
import { Stack } from '@mui/material';
import { captureEvent } from '@sentry/react';

import { EVENTS } from '@/analytics/consts';
import { GoalDto, StrategicPlanDto } from '@/api/generated';
import EditableMarkdown from '@/components/common/EditableTextMarkdown';
import {
  Container,
  GenerateButtonText,
  RegenerateButton
} from '@/pages/GoalsPage/components/StrategicPlanStyles';

interface StrategicPlanProps {
  selectedPeriod: { year: number; quarter: number };
  goals: GoalDto[];
  monthToStart: number;
  isGenerating: boolean;
  generatedStrategicPlan: string | null;
  isGenerateError: boolean;
  strategicPlan: StrategicPlanDto | undefined;
  onEditStrategicPlan: (content: string, id: string) => void;
  generatePlan: () => void;
}

const DEBOUNCE_TIME = 700;

const StrategicPlan = ({
  generatedStrategicPlan,
  strategicPlan,
  onEditStrategicPlan,
  generatePlan
}: StrategicPlanProps) => {
  const [localPlan, setLocalPlan] = useState('');

  const handleEdit = (content: string) => {
    setLocalPlan(content);
  };

  const handleSave = () => {
    if (strategicPlan?.id) {
      onEditStrategicPlan(localPlan, strategicPlan?.id);
    }
  };

  useDebounceCallback(localPlan, DEBOUNCE_TIME, handleSave);

  useEffect(() => {
    if (generatedStrategicPlan && strategicPlan?.id) {
      onEditStrategicPlan(generatedStrategicPlan, strategicPlan?.id!);
    }
  }, [generatedStrategicPlan, onEditStrategicPlan, strategicPlan]);

  return (
    <Container>
      <Stack>
        <RegenerateButton onClick={generatePlan}>
          <GenerateButtonText>Regenerate</GenerateButtonText>
        </RegenerateButton>
        <EditableMarkdown
          style={{ height: '100%', width: '100%' }}
          value={generatedStrategicPlan || strategicPlan?.plan || ''}
          callback={handleEdit}
        />
      </Stack>
    </Container>
  );
};

export default StrategicPlan;
